import { useQuery, useZero } from "@rocicorp/zero/react";
import { useState } from "react";
import { generateId } from "../../shared/id";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { CHANGE_REVIEW_TIMEOUT_MS } from "../../shared/publishedChangeReview";
import { useToast } from "../components/Toast";
import { useDeadlinePassed } from "./useDeadlinePassed";

export function useWorkflowChangeReviewData(commitId: string | null) {
  const [storedReview, query] = useQuery(
    queries.loadWorkflowChangeReview({ commitId: commitId ?? "" }),
    { enabled: commitId !== null }
  );
  const expired = useDeadlinePassed(
    storedReview?.status === "generating"
      ? storedReview.createdAt + CHANGE_REVIEW_TIMEOUT_MS
      : null
  );
  const review =
    commitId === null
      ? undefined
      : expired && storedReview
        ? { ...storedReview, status: "error" as const }
        : storedReview;
  return { review, loaded: query.type === "complete" };
}

export function useWorkflowChangeReview(version: {
  commitId: string | null;
  workflowId: string;
}) {
  const z = useZero();
  const { showToast } = useToast();
  const [pending, setPending] = useState(false);
  const { review, loaded } = useWorkflowChangeReviewData(version.commitId);
  const generating = pending || review?.status === "generating";
  const canGenerate = version.commitId !== null && loaded && !generating;
  const generate = async () => {
    if (!canGenerate || !version.commitId) return;
    setPending(true);
    try {
      const result = await z.mutate(
        sharedMutators.requestWorkflowChangeReview({
          id: generateId(),
          workflowId: version.workflowId,
          commitId: version.commitId,
        })
      ).server;
      if (result.type === "error") {
        showToast({
          icon: "circle-off",
          message: result.error.message,
          buttonLabel: "Dismiss",
        });
      }
    } catch {
      showToast({
        icon: "circle-off",
        message: "Couldn’t request a change review. Try again.",
        buttonLabel: "Dismiss",
      });
    }
    setPending(false);
  };
  return { review, generating, canGenerate, generate };
}
